/**
 * Lotus Product Detail Page — interactivity
 * Handles: thumbnail gallery, variant selection, quantity input
 */

(function () {
  'use strict';

  class LotusProduct {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;

      this.gallery = section.querySelector('#lotus-gallery-' + this.sectionId);
      this.thumbs = section.querySelectorAll('.lotus-product__thumb');
      this.mainImages = section.querySelectorAll('.lotus-product__main-image');

      this.form = section.querySelector('#lotus-product-form-' + this.sectionId);
      this.variantInput = section.querySelector('#lotus-variant-id-' + this.sectionId);
      this.optionButtons = section.querySelectorAll('.lotus-product__option-btn');
      this.addToCartBtn = section.querySelector('.lotus-product__add-to-cart');

      this.qtyInput = section.querySelector('#lotus-qty-' + this.sectionId);
      this.qtyMinusBtn = section.querySelector('[data-qty-action="minus"]');
      this.qtyPlusBtn = section.querySelector('[data-qty-action="plus"]');

      const variantsEl = section.querySelector('#lotus-variants-' + this.sectionId);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      this.selectedOptions = this._getInitialOptions();

      this._bindEvents();
      this._markUnavailableOptions();
      this._initThumbsSwiper();
    }

    _getInitialOptions() {
      const options = {};
      this.optionButtons.forEach((btn) => {
        if (btn.classList.contains('lotus-product__option-btn--selected')) {
          const pos = btn.dataset.optionPosition;
          options[pos] = btn.dataset.optionValue;
        }
      });
      return options;
    }

    _bindEvents() {
      // Thumbnail click → switch main image
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this._switchImage(parseInt(thumb.dataset.thumbIndex, 10));
        });
      });

      // Option button click → select variant
      this.optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('lotus-product__option-btn--unavailable')) return;
          this._selectOption(btn);
        });
      });

      // Quantity buttons
      if (this.qtyMinusBtn) {
        this.qtyMinusBtn.addEventListener('click', () => this._changeQty(-1));
      }
      if (this.qtyPlusBtn) {
        this.qtyPlusBtn.addEventListener('click', () => this._changeQty(1));
      }
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => {
          const val = parseInt(this.qtyInput.value, 10);
          if (isNaN(val) || val < 1) this.qtyInput.value = 1;
        });
      }
    }

    _switchImage(index) {
      this.thumbs.forEach((t) => t.classList.remove('lotus-product__thumb--active'));
      this.mainImages.forEach((img) => img.classList.remove('lotus-product__main-image--active'));

      const targetThumb = this.section.querySelector('[data-thumb-index="' + index + '"]');
      const targetImage = this.section.querySelector('[data-image-index="' + index + '"]');

      if (targetThumb) targetThumb.classList.add('lotus-product__thumb--active');
      if (targetImage) targetImage.classList.add('lotus-product__main-image--active');
    }

    _selectOption(btn) {
      const pos = btn.dataset.optionPosition;
      const val = btn.dataset.optionValue;
      const posNum = parseInt(pos, 10);

      // Deselect other buttons in the same option group
      this.section.querySelectorAll('[data-option-position="' + pos + '"]').forEach((b) => {
        b.classList.remove('lotus-product__option-btn--selected');
        b.setAttribute('aria-pressed', 'false');
      });

      btn.classList.add('lotus-product__option-btn--selected');
      btn.setAttribute('aria-pressed', 'true');

      this.selectedOptions[pos] = val;

      // Clear selections at later positions (e.g. changing color resets size)
      Object.keys(this.selectedOptions).forEach((p) => {
        if (parseInt(p, 10) > posNum) {
          delete this.selectedOptions[p];
          this.section.querySelectorAll('[data-option-position="' + p + '"]').forEach((b) => {
            b.classList.remove('lotus-product__option-btn--selected');
            b.setAttribute('aria-pressed', 'false');
          });
        }
      });

      this._markUnavailableOptions();
      this._updateVariant();
    }

    _getTotalOptionPositions() {
      const positions = new Set();
      this.optionButtons.forEach((btn) => {
        positions.add(btn.dataset.optionPosition);
      });
      return positions.size;
    }

    _updateVariant() {
      const totalOptions = this._getTotalOptionPositions();
      const selectedCount = Object.keys(this.selectedOptions).length;
      const allSelected = totalOptions > 0 && selectedCount === totalOptions;

      const matchesSelection = (v) => Object.entries(this.selectedOptions).every(([pos, val]) => {
        const idx = parseInt(pos, 10) - 1;
        return v.options[idx] === val;
      });

      const exactVariant = allSelected ? this.variants.find(matchesSelection) : null;
      const partialVariant = this.variants.find(matchesSelection);

      if (this.variantInput && exactVariant) {
        this.variantInput.value = exactVariant.id;
      }

      if (this.addToCartBtn) {
        if (exactVariant && exactVariant.available) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.classList.remove('lotus-product__add-to-cart--disabled');
          this.addToCartBtn.textContent = 'ADD TO CART';
        } else if (exactVariant && !exactVariant.available) {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.classList.add('lotus-product__add-to-cart--disabled');
          this.addToCartBtn.textContent = 'SOLD OUT';
        } else {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.classList.add('lotus-product__add-to-cart--disabled');
          this.addToCartBtn.textContent = 'ADD TO CART';
        }
      }

      if (partialVariant && partialVariant.featured_image) {
        const imgId = partialVariant.featured_image.id;
        const imageIndex = Array.from(this.mainImages).findIndex((img) => {
          const src = img.querySelector('img');
          return src && src.src.includes(imgId);
        });
        if (imageIndex >= 0) {
          this._switchImage(imageIndex);
        }
      }
    }

    _markUnavailableOptions() {
      // A value at position N is unavailable when no available variant matches
      // both this value and all currently-selected values at positions 1..N-1.
      this.optionButtons.forEach((btn) => {
        const pos = parseInt(btn.dataset.optionPosition, 10);
        const idx = pos - 1;
        const val = btn.dataset.optionValue;

        const hasAvailable = this.variants.some((v) => {
          if (v.options[idx] !== val) return false;
          if (!v.available) return false;
          for (let i = 1; i < pos; i++) {
            const selected = this.selectedOptions[i];
            if (selected != null && v.options[i - 1] !== selected) return false;
          }
          return true;
        });

        if (!hasAvailable) {
          btn.classList.add('lotus-product__option-btn--unavailable');
        } else {
          btn.classList.remove('lotus-product__option-btn--unavailable');
        }
      });
    }

    _initThumbsSwiper() {
      var swiperEl = this.section.querySelector('.js-product-thumbs-swiper');
      if (!swiperEl) return;

      var swiper = null;
      var mq = window.matchMedia('(max-width: 749px)');

      function update(e) {
        if (e.matches) {
          if (!swiper) {
            swiper = new Swiper(swiperEl, {
              slidesPerView: 'auto',
              freeMode: true,
              loop: false,
              spaceBetween: 10
            });
          }
        } else {
          if (swiper) {
            swiper.destroy(true, true);
            swiper = null;
          }
        }
      }

      mq.addEventListener('change', update);
      update(mq);
    }

    _changeQty(delta) {
      if (!this.qtyInput) return;
      const current = parseInt(this.qtyInput.value, 10) || 1;
      const next = Math.max(1, current + delta);
      this.qtyInput.value = next;
    }
  }

  // Initialize on DOM ready
  function init() {
    document.querySelectorAll('[data-section-id].lotus-product').forEach((section) => {
      new LotusProduct(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
